import { StringData } from "../.."
import { StringContext } from "../../core/StringContext"
import { StringModule } from "../../core/StringModule"

/**
 * Visual tracker that plots lerped scroll velocity (v) in real time.
 * Useful for analyzing smooth scroll interpolation behavior.
 */
export class StringLerpTracker extends StringModule {
  private canvas!: HTMLCanvasElement
  private context!: CanvasRenderingContext2D
  private history: number[] = []

  private maxPoints = 0
  private canvasHeight = 0
  private currentValue = 0
  private targetValue = 0

  constructor(context: StringContext) {
    super(context)
    this._type = 2
  }

  /**
   * Called on start — sets up canvas overlay.
   */
  onInit(): void {
    this.initCanvas()
    this.maxPoints = this.canvas.width
  }

  /**
   * Called on scroll — reads smoothed scroll velocity (v).
   */
  onScroll(data: StringData): void {
    const v = Math.abs(data.scroll.displacement)
    this.currentValue = v
    this.history.push(v)

    if (this.history.length > this.maxPoints) {
      this.history.shift()
    }

    this.draw()
  }

  /**
   * Draws the current graph line based on v-history.
   */
  private draw(): void {
    const ctx = this.context
    const w = this.canvas.width
    const h = this.canvas.height

    ctx.clearRect(0, 0, w, h)

    ctx.strokeStyle = "#007bff"
    ctx.lineWidth = 2
    ctx.beginPath()

    this.history.forEach((val, i) => {
      const x = i
      const y = h - val / 2
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    })

    ctx.stroke()
  }

  /**
   * Creates the canvas overlay and applies style.
   */
  private initCanvas(): void {
    this.canvas = document.createElement("canvas")
    this.canvasHeight = window.innerHeight / 15 - 20
    this.canvas.width = window.innerWidth * 0.5
    this.canvas.height = this.canvasHeight

    Object.assign(this.canvas.style, {
      position: "fixed",
      bottom: "10px",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "#000",
      border: "1px solid rgba(255,255,255,0.2)",
      zIndex: "1000",
      pointerEvents: "none",
    })

    this.context = this.canvas.getContext("2d")!
    document.body.appendChild(this.canvas)
  }

  /**
   * Optional external target value for debugging.
   */
  public setTarget(position: number): void {
    this.targetValue = position
  }

  /**
   * Removes canvas from DOM and clears history.
   */
  public clear(): void {
    this.canvas.remove()
    this.history = []
  }
}
